import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "@/lib/toast";
import { useAuth } from "../lib/auth";

export default function GoogleLoginButton() {
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  const { googleLogin } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const containerRef = useRef(null);
  const renderedRef = useRef(false);
  const [isSigningIn, setIsSigningIn] = useState(false);

  useEffect(() => {
    if (!clientId) return;
    renderedRef.current = false;

    const render = () => {
      if (renderedRef.current) return;
      if (!window.google?.accounts?.id) return;
      if (!containerRef.current) return;

      renderedRef.current = true;
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: async (response) => {
          setIsSigningIn(true);
          try {
            await googleLogin(response.credential);
            toast.success("Signed in with Google");
            const returnTo = searchParams.get("return");
            navigate(returnTo && returnTo.startsWith("/") && !returnTo.startsWith("//") ? returnTo : "/app");
          } catch (err) {
            toast.error(err?.response?.data?.detail || "Google sign-in failed");
            setIsSigningIn(false);
          }
        },
      });
      window.google.accounts.id.renderButton(containerRef.current, {
        type: "standard",
        shape: "pill",
        theme: "outline",
        size: "large",
        text: "continue_with",
        width: Math.max(containerRef.current.offsetWidth || 0, 300),
        locale: "en",
      });
    };

    // If GSI already loaded, render immediately
    if (window.google?.accounts?.id) {
      render();
      return;
    }

    // Otherwise wait for the script to load, with polling fallback
    const script = document.getElementById("google-gsi-script");
    if (script) {
      script.addEventListener("load", render);
    }

    // Poll every 200ms up to 5s in case the load event already fired
    let attempts = 0;
    const poll = setInterval(() => {
      if (window.google?.accounts?.id) {
        clearInterval(poll);
        render();
      } else if (++attempts > 25) {
        clearInterval(poll);
      }
    }, 200);

    return () => {
      clearInterval(poll);
      if (script) script.removeEventListener("load", render);
    };
  }, [clientId, googleLogin, navigate, searchParams]);

  if (!clientId) return null;

  return (
    <div className="relative w-full" aria-busy={isSigningIn}>
      <div
        ref={containerRef}
        className={`w-full flex justify-center transition-opacity ${isSigningIn ? "pointer-events-none opacity-50" : ""}`}
        style={{ minHeight: 44 }}
      />
      {isSigningIn && (
        <div className="absolute inset-0 flex items-center justify-center" role="status" aria-live="polite">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/95 px-4 py-2 text-sm font-medium text-[#6B6158] shadow-sm ring-1 ring-[#E8DFD1]">
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-[#C96F53]/30 border-t-[#C96F53]" aria-hidden="true" />
            Signing you in…
          </span>
        </div>
      )}
    </div>
  );
}
